/**
 * Drawings without a platform: the built-in silhouette that stands in for a
 * missing drawing, and the resolution of one of the seven views onto its
 * drawing. Loading real drawings is platform work and injected (the web's
 * loadDrawings.ts, the native app's bundled PNGs, the API adapter's
 * DrawingTransport).
 */
import { BASE_VIEWS, CABIN_VIEW, resolveView, type AircraftView, type BaseView } from '@fsv/core';
import type { AircraftType, Drawing } from '../data/types';
import { cabin, layoutForType, seatsForType, silhouette, svgDataUrl } from './silhouettes';

/** The built-in silhouette, marked as placeholder; also used by the API adapter for types without a drawing. */
export function placeholderDrawing(typeCode: string, view: BaseView): Drawing {
  const s = silhouette(layoutForType(typeCode), view);
  return { url: svgDataUrl(s.svg), width: s.width, height: s.height, placeholder: true };
}

/** The cabin schematic for the cabin view (innen), marked as placeholder. */
export function cabinPlaceholder(typeCode: string): Drawing {
  const s = cabin(seatsForType(typeCode));
  return { url: svgDataUrl(s.svg), width: s.width, height: s.height, placeholder: true };
}

/** All three base views as silhouettes: the drawing loader of last resort. */
export function placeholderDrawings(typeCode: string): Record<BaseView, Drawing> {
  return Object.fromEntries(BASE_VIEWS.map((v) => [v, placeholderDrawing(typeCode, v)])) as Record<BaseView, Drawing>;
}

export interface ResolvedDrawing {
  drawing: Drawing;
  mirrored: boolean;
  /** The view the image belongs to: a base view, or the cabin (innen). */
  base: BaseView | typeof CABIN_VIEW;
  /** height / width, the factor every distance on this view needs. */
  aspect: number;
  type: AircraftType;
}

/**
 * One of the seven views onto its drawing. Mirroring is rendering only
 * (spec 7): the coordinates of a pin refer to the view as it appears on screen.
 */
export function resolveDrawing(type: AircraftType, view: AircraftView): ResolvedDrawing {
  if (view === CABIN_VIEW) {
    // The cabin is never drawn from another view: its own image, else the cabin schematic.
    const drawing = type.drawings.innen ?? cabinPlaceholder(type.code);
    return { drawing, mirrored: false, base: CABIN_VIEW, aspect: drawing.height / drawing.width, type };
  }
  const { base, mirrored } = resolveView(view, type.mirrorRules);
  const key = (BASE_VIEWS as readonly string[]).includes(base) ? (base as BaseView) : 'oben';
  // A derived view the club gave its own image (unten, rechts, hinten) is not in the mirror
  // rules and carries its drawing under its own key; only without one is `oben` the fallback.
  const drawing = (!mirrored ? type.drawings[base] : undefined) ?? type.drawings[key];
  return { drawing, mirrored, base: key, aspect: drawing.height / drawing.width, type };
}
